(() => {
  const ROOT_SELECTOR = '[data-pm-order-builder]';
  const initialized = new WeakSet();
  const dateRules = window.PetMealsDeliveryDateRules;
  const core = window.PetMealsOrderBuilderCore;
  const deliveryFees = window.PetMealsDeliveryFees;
  const deliveryConfig = window.PetMealsDeliveryConfig || {};
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const monthFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const serverTimeMs = Number(deliveryConfig.serverTimeMs);
  const monotonicStart = window.performance?.now?.() || 0;

  if (!dateRules || !core || !deliveryFees) return;

  function getCurrentTimeMs() {
    const debugTimeMs = Number(window.__PM_DEBUG_TIME_OVERRIDE_MS__);
    if (Number.isFinite(debugTimeMs) && debugTimeMs > 0) return debugTimeMs;
    if (!Number.isFinite(serverTimeMs)) return Date.now();
    return serverTimeMs + Math.max(0, (window.performance?.now?.() || 0) - monotonicStart);
  }

  function formatMoney(cents, currency) {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: currency || 'PHP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Math.max(0, Number(cents) || 0) / 100);
  }

  function parseMoneyToCents(value) {
    const normalized = String(value || '').replace(/,/g, '');
    const amount = Number(normalized.replace(/[^\d.]/g, ''));
    return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
  }

  function getShippingFee(root, area) {
    return deliveryFees.getFeeForArea(area, root.dataset.shippingFees);
  }

  function buildDateKey(year, monthIndex, day) {
    return `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  function createCalendar({ mount, selectedDates, getDeliveryPeriod, copy, onChange }) {
    const initialParts = dateRules.parseDateKey(
      dateRules.getEarliestSelectableDateKey(getCurrentTimeMs(), getDeliveryPeriod())
    );
    let visibleYear = initialParts.year;
    let visibleMonth = initialParts.month - 1;
    let availabilitySignature = '';

    function getEarliestParts() {
      return dateRules.parseDateKey(
        dateRules.getEarliestSelectableDateKey(getCurrentTimeMs(), getDeliveryPeriod())
      );
    }

    function isVisibleBefore(year, monthIndex, parts) {
      return year < parts.year || (year === parts.year && monthIndex < parts.month - 1);
    }

    function removeUnavailableDates() {
      const now = getCurrentTimeMs();
      const period = getDeliveryPeriod();
      let changed = false;

      selectedDates.forEach((dateKey) => {
        if (dateRules.isDeliveryDateAvailable(dateKey, now, period)) return;
        selectedDates.delete(dateKey);
        changed = true;
      });

      return changed;
    }

    function render() {
      const now = getCurrentTimeMs();
      const period = getDeliveryPeriod();
      const earliestParts = getEarliestParts();
      const todayKey = dateRules.getManilaDateKey(now);

      if (isVisibleBefore(visibleYear, visibleMonth, earliestParts)) {
        visibleYear = earliestParts.year;
        visibleMonth = earliestParts.month - 1;
      }

      mount.replaceChildren();
      mount.className = 'pm-delivery-calendar pm-order-builder__calendar';

      const navigation = document.createElement('div');
      navigation.className = 'pm-delivery-calendar__nav';

      const previousButton = document.createElement('button');
      previousButton.className = 'pm-delivery-calendar__nav-button';
      previousButton.type = 'button';
      previousButton.textContent = '‹';
      previousButton.setAttribute('aria-label', copy.previous_month);
      previousButton.disabled =
        visibleYear === earliestParts.year && visibleMonth === earliestParts.month - 1;

      const title = document.createElement('div');
      title.className = 'pm-delivery-calendar__title';
      title.textContent = monthFormatter.format(new Date(Date.UTC(visibleYear, visibleMonth, 1)));

      const nextButton = document.createElement('button');
      nextButton.className = 'pm-delivery-calendar__nav-button';
      nextButton.type = 'button';
      nextButton.textContent = '›';
      nextButton.setAttribute('aria-label', copy.next_month);

      navigation.append(previousButton, title, nextButton);

      const weekdayRow = document.createElement('div');
      weekdayRow.className = 'pm-delivery-calendar__weekdays';
      weekdays.forEach((weekday) => {
        const cell = document.createElement('div');
        cell.className = 'pm-delivery-calendar__weekday';
        cell.textContent = weekday;
        weekdayRow.appendChild(cell);
      });

      const grid = document.createElement('div');
      grid.className = 'pm-delivery-calendar__grid';
      const firstWeekday = new Date(Date.UTC(visibleYear, visibleMonth, 1)).getUTCDay();
      const daysInMonth = new Date(Date.UTC(visibleYear, visibleMonth + 1, 0)).getUTCDate();

      for (let index = 0; index < firstWeekday; index += 1) {
        const blank = document.createElement('span');
        blank.className = 'pm-delivery-calendar__blank';
        grid.appendChild(blank);
      }

      for (let day = 1; day <= daysInMonth; day += 1) {
        const dateKey = buildDateKey(visibleYear, visibleMonth, day);
        const isAvailable = dateRules.isDeliveryDateAvailable(dateKey, now, period);
        const button = document.createElement('button');
        const dateLabel = dateRules.formatDateKey(dateKey);

        button.className = 'pm-delivery-calendar__day';
        button.type = 'button';
        button.textContent = String(day);
        button.disabled = !isAvailable;
        button.setAttribute(
          'aria-label',
          `${dateLabel}${isAvailable ? '' : `, ${copy.unavailable}`}`
        );
        button.setAttribute('aria-pressed', String(selectedDates.has(dateKey)));

        if (dateKey === todayKey) button.classList.add('pm-delivery-calendar__day--today');
        if (selectedDates.has(dateKey)) {
          button.classList.add('pm-delivery-calendar__day--selected');
        }
        if (!isAvailable) {
          button.classList.add('pm-delivery-calendar__day--disabled');
        } else {
          button.addEventListener('click', () => {
            if (selectedDates.has(dateKey)) {
              selectedDates.delete(dateKey);
            } else {
              selectedDates.add(dateKey);
            }
            onChange();
            render();
          });
        }

        grid.appendChild(button);
      }

      previousButton.addEventListener('click', () => {
        visibleMonth -= 1;
        if (visibleMonth < 0) {
          visibleMonth = 11;
          visibleYear -= 1;
        }
        render();
      });

      nextButton.addEventListener('click', () => {
        visibleMonth += 1;
        if (visibleMonth > 11) {
          visibleMonth = 0;
          visibleYear += 1;
        }
        render();
      });

      mount.append(navigation, weekdayRow, grid);
    }

    function refresh() {
      const changed = removeUnavailableDates();
      render();
      if (changed) onChange();
    }

    function getSignature() {
      const now = getCurrentTimeMs();
      const period = getDeliveryPeriod();
      return [
        dateRules.getManilaDateKey(now),
        dateRules.getMinimumDeliveryDateKey(now, period),
        dateRules.getDeliveryPeriodTiming(period),
      ].join('|');
    }

    availabilitySignature = getSignature();
    render();

    const timer = window.setInterval(() => {
      if (!mount.isConnected) {
        window.clearInterval(timer);
        return;
      }

      const nextSignature = getSignature();
      if (nextSignature === availabilitySignature) return;
      availabilitySignature = nextSignature;
      refresh();
    }, 30000);

    return { render, refresh };
  }

  function initBuilder(root) {
    if (initialized.has(root)) return;

    const copyNode = root.querySelector('[data-pm-order-builder-copy]');
    const copy = JSON.parse(copyNode?.textContent || '{}');
    const currency = root.dataset.currency || 'PHP';
    const selectedDates = new Set();
    const status = root.querySelector('[data-pm-builder-status]');
    const error = root.querySelector('[data-pm-builder-error]');
    const submitButton = root.querySelector('[data-pm-builder-submit]');
    const furbabyInput = root.querySelector('[data-pm-builder-furbaby]');
    const areaSelect = root.querySelector('[data-pm-builder-area]');
    const periodInputs = Array.from(root.querySelectorAll('[data-pm-builder-period]'));
    const dateList = root.querySelector('[data-pm-builder-date-list]');
    const summaryRecipes = root.querySelector('[data-pm-summary-recipes]');
    const summaryPerDelivery = root.querySelector('[data-pm-summary-packs-per-delivery]');
    const summaryDates = root.querySelector('[data-pm-summary-date-count]');
    const summaryTotalPacks = root.querySelector('[data-pm-summary-total-packs]');
    const summaryFees = root.querySelector('[data-pm-summary-fees]');
    const summaryTotal = root.querySelector('[data-pm-summary-grand-total]');
    const summaryLines = root.querySelector('[data-pm-summary-lines]');
    const summaryDetails = root.querySelector('[data-pm-summary-details]');
    const actionTotal = root.querySelector('[data-pm-action-total]');
    let submitting = false;
    let validationWasRequested = false;
    let calendar;

    const products = Array.from(root.querySelectorAll('[data-pm-builder-product]')).map((card) => {
      const data = JSON.parse(card.querySelector('[data-pm-product-json]')?.textContent || '{}');
      const quantityValue = card.querySelector('[data-pm-product-quantity]');
      const decreaseButton = card.querySelector('[data-pm-quantity-decrease]');
      const increaseButton = card.querySelector('[data-pm-quantity-increase]');
      const price = card.querySelector('[data-pm-product-price]');
      const selectionStatus = card.querySelector('[data-pm-product-selection-status]');
      const packInputs = Array.from(card.querySelectorAll('[data-pm-pack-option]'));
      let quantity = 0;

      function getPackValue() {
        return packInputs.find((input) => input.checked)?.value || packInputs[0]?.value || '';
      }

      function getVariant() {
        const deliveryPeriod = getDeliveryPeriod();
        return (
          core.resolveVariant(data, getPackValue(), deliveryPeriod, true) ||
          (!deliveryPeriod ? core.resolveVariant(data, getPackValue(), '', true) : null)
        );
      }

      function update() {
        const deliveryPeriod = getDeliveryPeriod();
        packInputs.forEach((input) => {
          const variant = core.resolveVariant(data, input.value, deliveryPeriod, true);
          input.disabled = Boolean(deliveryPeriod && !variant);
          input.closest('label')?.classList.toggle('is-unavailable', input.disabled);
        });

        const variant = getVariant();
        quantityValue.textContent = String(quantity);
        decreaseButton.disabled = quantity <= 0;
        increaseButton.disabled = !variant;
        card.classList.toggle('is-selected', quantity > 0);
        card.classList.toggle('is-unavailable', !variant);
        price.textContent = variant ? formatMoney(variant.price, currency) : copy.unavailable;
        selectionStatus.textContent = quantity > 0 ? copy.in_order : copy.add_with_plus;
      }

      decreaseButton.addEventListener('click', () => {
        quantity = Math.max(0, quantity - 1);
        update();
        updateState();
      });

      increaseButton.addEventListener('click', () => {
        if (!getVariant()) return;
        quantity += 1;
        update();
        updateState();
      });

      packInputs.forEach((input) => {
        input.addEventListener('change', () => {
          update();
          updateState();
        });
      });

      return {
        card,
        data,
        get quantity() {
          return quantity;
        },
        getPackValue,
        getVariant,
        update,
      };
    });

    function getDeliveryPeriod() {
      return periodInputs.find((input) => input.checked)?.value || '';
    }

    function getArea() {
      if (!areaSelect.value) return null;
      const option = areaSelect.options[areaSelect.selectedIndex];
      return {
        slug: areaSelect.value,
        name: option?.dataset.areaName || option?.textContent?.trim() || areaSelect.value,
      };
    }

    function getSelections() {
      return products
        .filter((product) => product.quantity > 0)
        .map((product) => {
          const variant = product.getVariant();
          return {
            productTitle: product.data.title,
            variantId: variant?.id,
            price: variant?.price || 0,
            quantity: product.quantity,
            packValue: product.getPackValue(),
          };
        });
    }

    function syncDeliveryPeriodAvailability() {
      const now = getCurrentTimeMs();
      const candidateDates =
        selectedDates.size > 0
          ? Array.from(selectedDates)
          : [dateRules.getEarliestSelectableDateKey(now)];

      periodInputs.forEach((input) => {
        if (dateRules.getDeliveryPeriodTiming(input.value) !== 'day_before') return;
        input.disabled = !candidateDates.every((dateKey) =>
          dateRules.isDeliveryDateAvailable(dateKey, now, input.value)
        );
        input.closest('label')?.classList.toggle('is-unavailable', input.disabled);
      });

      const selectedPeriod = periodInputs.find((input) => input.checked);
      if (selectedPeriod?.disabled) {
        selectedPeriod.checked = false;
        const sameDayInput = periodInputs.find(
          (input) => dateRules.getDeliveryPeriodTiming(input.value) === 'same_day' && !input.disabled
        );
        if (sameDayInput) sameDayInput.checked = true;
      }
    }

    function renderDateList() {
      dateList.replaceChildren();
      const sortedDates = Array.from(selectedDates).sort();

      if (sortedDates.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'pm-order-builder__date-empty';
        empty.textContent = copy.no_dates;
        dateList.appendChild(empty);
        return;
      }

      sortedDates.forEach((dateKey) => {
        const chip = document.createElement('span');
        chip.className = 'pm-order-builder__date-chip';

        const label = document.createElement('span');
        label.textContent = dateRules.formatDateKey(dateKey);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.textContent = '×';
        remove.setAttribute('aria-label', `${copy.remove_date} ${label.textContent}`);
        remove.addEventListener('click', () => {
          selectedDates.delete(dateKey);
          syncDeliveryPeriodAvailability();
          calendar.render();
          updateState();
        });

        chip.append(label, remove);
        dateList.appendChild(chip);
      });
    }

    function getValidationMessage() {
      const selections = getSelections();
      const deliveryPeriod = getDeliveryPeriod();
      const now = getCurrentTimeMs();

      if (selections.length === 0) return copy.choose_recipe;
      if (selections.some((selection) => !selection.variantId)) return copy.choose_available_pack;
      if (!furbabyInput.value.trim()) return copy.enter_furbaby;
      if (!getArea()) return copy.choose_area;
      if (!deliveryFees.hasConfiguredVariant(getShippingFee(root, getArea()))) {
        return copy.delivery_fee_unconfigured;
      }
      if (!deliveryPeriod) return copy.choose_period;
      if (selectedDates.size === 0) return copy.choose_dates;
      if (
        Array.from(selectedDates).some(
          (dateKey) => !dateRules.isDeliveryDateAvailable(dateKey, now, deliveryPeriod)
        )
      ) {
        return copy.dates_changed;
      }
      return '';
    }

    function updateSummary() {
      const selections = getSelections();
      const dateCount = selectedDates.size;
      const shippingFee = getShippingFee(root, getArea());
      const totals = core.getOrderTotals({
        selections,
        dateCount,
        shippingFeeCents: shippingFee.cents,
      });

      summaryRecipes.textContent = String(totals.recipeCount);
      summaryPerDelivery.textContent = String(totals.packsPerDelivery);
      summaryDates.textContent = String(dateCount);
      summaryTotalPacks.textContent = String(totals.totalPacks);
      root.dataset.shippingFeeVariantId = shippingFee.variantId;
      root.dataset.shippingFeeLabel = shippingFee.label;
      summaryFees.textContent = `${formatMoney(totals.deliveryTotalCents, currency)} (${dateCount} × ${shippingFee.label})`;
      summaryTotal.textContent = formatMoney(totals.grandTotalCents, currency);
      actionTotal.textContent = formatMoney(totals.grandTotalCents, currency);
      summaryLines.replaceChildren();

      selections.forEach((selection) => {
        const line = document.createElement('li');
        const packCount = core.getPackCount(selection.packValue);
        const physicalPacks = packCount * selection.quantity;
        line.innerHTML = `<span></span><strong></strong>`;
        line.querySelector('span').textContent = `${selection.productTitle} · ${selection.packValue} × ${selection.quantity}`;
        line.querySelector('strong').textContent = `${physicalPacks} ${copy.packs_per_delivery}`;
        summaryLines.appendChild(line);
      });
    }

    function updateState() {
      syncDeliveryPeriodAvailability();
      products.forEach((product) => product.update());
      renderDateList();
      updateSummary();

      const message = validationWasRequested ? getValidationMessage() : '';
      error.textContent = message;
      error.hidden = !message;
      submitButton.disabled = submitting;
      submitButton.setAttribute('aria-disabled', String(submitting));
      submitButton.querySelector('span').textContent = submitting ? copy.adding : copy.review_order;
    }

    periodInputs.forEach((input) => {
      input.addEventListener('change', () => {
        calendar.refresh();
        updateState();
      });
    });
    furbabyInput.addEventListener('input', updateState);
    areaSelect.addEventListener('change', updateState);

    root.querySelectorAll('[data-pm-details-open]').forEach((button) => {
      button.addEventListener('click', () => {
        const dialog = document.getElementById(button.getAttribute('aria-controls'));
        dialog?.showModal();
      });
    });

    root.querySelectorAll('[data-pm-details-close]').forEach((button) => {
      button.addEventListener('click', () => button.closest('dialog')?.close());
    });

    root.querySelectorAll('dialog').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) dialog.close();
      });
    });

    root.addEventListener('submit', async (event) => {
      if (!event.target.matches('[data-pm-builder-form]')) return;
      event.preventDefault();
      if (submitting) return;

      validationWasRequested = true;
      const message = getValidationMessage();
      updateState();
      if (message) {
        error.focus();
        return;
      }

      submitting = true;
      status.textContent = copy.checking_cart;
      updateState();

      try {
        const cartResponse = await fetch(`${root.dataset.cartUrl}.js`, {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!cartResponse.ok) throw new Error(copy.cart_read_failed);
        const cart = await cartResponse.json();
        const area = getArea();
        const deliveryPeriod = getDeliveryPeriod();
        const formattedDates = Array.from(selectedDates)
          .sort()
          .map((dateKey) => dateRules.formatDateKey(dateKey));
        const compatibilityIssue = core.getCartCompatibilityError(cart, {
          dates: formattedDates,
          areaSlug: area.slug,
          deliveryPeriod,
        });

        if (compatibilityIssue) {
          throw new Error(copy[`cart_${compatibilityIssue}`]);
        }

        const shippingFee = getShippingFee(root, area);
        const payload = core.buildOrderPayload({
          selections: getSelections(),
          dates: formattedDates,
          area,
          furbabyName: furbabyInput.value.trim(),
          deliveryPeriod,
          shippingFeeVariantId: shippingFee.variantId,
          shippingFeeLabel: shippingFee.label,
          existingShippingDates: core.getExistingShippingDates(cart),
        });
        const addResponse = await fetch(root.dataset.cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: payload.items }),
        });
        const addResult = await addResponse.json().catch(() => ({}));
        if (!addResponse.ok || addResult.errors) {
          throw new Error(addResult.description || addResult.message || copy.cart_add_failed);
        }

        status.textContent = copy.added;
        window.location.assign(root.dataset.cartUrl);
      } catch (caughtError) {
        submitting = false;
        updateState();
        error.textContent = caughtError.message || copy.cart_add_failed;
        error.hidden = false;
        error.focus();
        status.textContent = '';
      }
    });

    calendar = createCalendar({
      mount: root.querySelector('[data-pm-builder-calendar]'),
      selectedDates,
      getDeliveryPeriod,
      copy,
      onChange() {
        syncDeliveryPeriodAvailability();
        updateState();
      },
    });

    if (window.matchMedia('(max-width: 749px)').matches) {
      summaryDetails.open = false;
    }

    initialized.add(root);
    updateState();
  }

  function initialize() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
